import { useState, useEffect, useRef } from 'react';
import { Mail, Phone, CheckCircle, Shield, X, Eye, Lock, ScrollText, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { usePdfReadConfirmation } from './hooks/usePdfReadConfirmation';
import { ScrollablePdfReader } from './ScrollablePdfReader';

const PRIVACY_UNAVAILABLE_MSG = {
  IT: 'L\'informativa privacy non è ancora disponibile per il tuo paese. Contatta l\'assistenza LoJack.',
  UK: 'The privacy notice is not yet available for your country. Please contact LoJack support.',
  IE: 'The privacy notice is not yet available for your country. Please contact LoJack support.',
  FR: 'La notice de confidentialité n\'est pas encore disponible pour votre pays. Veuillez contacter le service client LoJack.',
  ES: 'El aviso de privacidad aún no está disponible para tu país. Ponte en contacto con el soporte de LoJack.',
  DE: 'Die Datenschutzerklärung ist für Ihr Land noch nicht verfügbar. Bitte wenden Sie sich an den LoJack-Support.',
  AT: 'Die Datenschutzerklärung ist für Ihr Land noch nicht verfügbar. Bitte wenden Sie sich an den LoJack-Support.',
  PT: 'O aviso de privacidade ainda não está disponível para o seu país. Contacte o apoio LoJack.',
  PL: 'Informacja o prywatności nie jest jeszcze dostępna dla Twojego kraju. Skontaktuj się z pomocą techniczną LoJack.',
  NL: 'De privacyverklaring is nog niet beschikbaar voor uw land. Neem contact op met LoJack-support.',
};

const PrivacyUnavailableNotice = ({ country }) => (
  <div
    className="flex flex-col items-center justify-center text-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-6"
    style={{ minHeight: 200 }}
    data-testid="gdpr-privacy-unavailable"
  >
    <AlertTriangle size={28} className="text-amber-400" />
    <p className="text-sm text-amber-100 max-w-md">
      {PRIVACY_UNAVAILABLE_MSG[(country || 'UK').toUpperCase()] || PRIVACY_UNAVAILABLE_MSG.UK}
    </p>
  </div>
);


const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Req. A. Zorzutti / LoJack Legal Stand-up 05/06/2026:
//   "Teniamo lo sniffer sullo scroll down dell'utente nella versione
//    desktop, mentre facciamo il downgrade al countdown SOLO nella
//    versione mobile."
//
// Replaces the old fixed 15s countdown with the shared
// `usePdfReadConfirmation` hook:
//   - DESKTOP → scroll-end detection on iframe
//   - MOBILE  → 15s countdown fallback
const PRIVACY_MIN_READ_SECONDS = 15;

// Translations for the GDPR preferences step.
// Req. Zorzutti Stand-up 22/05/2026: ridotto a 2 opzioni (Sondaggi + Marketing)
// con UI tap-to-toggle (Accept/Reject), nessuna checkbox classica.
const GDPR_TRANSLATIONS = {
  IT: {
    privacySectionLabel: 'Informativa privacy',
    privacyTitle: 'Privacy',
    privacyText: 'Autorizzo il trattamento dei dati personali sopra riportati ai sensi del Regolamento UE 2016/679 GDPR',
    privacyRequired: 'Il consenso privacy è obbligatorio',
    optionsTitle: 'Cosa accetti di ricevere?',
    survey: 'Sondaggi e questionari',
    surveyDesc: 'Ricerche di mercato e questionari sui prodotti',
    marketing: 'Azioni di marketing',
    marketingDesc: 'Newsletter, offerte promozionali e novità LoJack',
    accepted: 'Accettato',
    rejected: 'Rifiutato',
    tapToAccept: 'Tocca per accettare',
    contactMethodTitle: 'Su quale canale vuoi essere contattato?',
    contactMethodHint: 'Sceglierai il canale solo se hai accettato almeno una delle opzioni sopra',
    viewPrivacyLink: "Visualizza l'Informativa per la privacy",
    contactChannelsHint: 'Puoi selezionare uno o più canali. Saranno usati per le comunicazioni di marketing accettate sopra.',
    email: 'E-mail',
    phone: 'Telefono',
    phone1: 'Cellulare 1',
    phone2: 'Cellulare 2',
    required: 'I campi contrassegnati con * sono obbligatori',
    confirm: 'Conferma Preferenze',
    saving: 'Salvataggio...',
    success: 'Preferenze salvate',
  },
  UK: {
    privacySectionLabel: 'Privacy notice',
    privacyTitle: 'Privacy',
    privacyText: 'I authorize the processing of my personal data in accordance with EU Regulation 2016/679 GDPR',
    privacyRequired: 'Privacy consent is mandatory',
    optionsTitle: 'What do you accept to receive?',
    survey: 'Surveys and questionnaires',
    surveyDesc: 'Market research and product questionnaires',
    marketing: 'Marketing actions',
    marketingDesc: 'Newsletter, promotional offers and LoJack news',
    accepted: 'Accepted',
    rejected: 'Rejected',
    tapToAccept: 'Tap to accept',
    contactMethodTitle: 'Which channel would you like to be contacted on?',
    contactMethodHint: 'You will pick the channel only if you accepted at least one option above',
    viewPrivacyLink: 'View the Privacy Information',
    contactChannelsHint: 'You can select one or more channels. They will be used for the marketing communications you accepted above.',
    email: 'Email',
    phone: 'Phone',
    phone1: 'Phone 1',
    phone2: 'Phone 2',
    required: 'Fields marked with * are mandatory',
    confirm: 'Confirm Preferences',
    saving: 'Saving...',
    success: 'Preferences saved',
  },
  FR: {
    privacySectionLabel: "Information sur la confidentialité",
    privacyTitle: 'Confidentialité',
    privacyText: "J'autorise le traitement de mes données personnelles conformément au Règlement UE 2016/679 RGPD",
    privacyRequired: 'Le consentement est obligatoire',
    optionsTitle: 'Que souhaitez-vous recevoir ?',
    survey: 'Sondages et questionnaires',
    surveyDesc: 'Études de marché et questionnaires produits',
    marketing: 'Actions marketing',
    marketingDesc: 'Newsletter, offres promotionnelles et actualités LoJack',
    accepted: 'Accepté',
    rejected: 'Refusé',
    tapToAccept: 'Touchez pour accepter',
    contactMethodTitle: 'Sur quel canal souhaitez-vous être contacté ?',
    contactMethodHint: 'Vous choisirez le canal uniquement si vous avez accepté au moins une option ci-dessus',
    viewPrivacyLink: "Voir l'Information sur la confidentialité",
    contactChannelsHint: 'Vous pouvez sélectionner un ou plusieurs canaux. Ils seront utilisés pour les communications marketing acceptées ci-dessus.',
    email: 'E-mail',
    phone: 'Téléphone',
    phone1: 'Téléphone 1',
    phone2: 'Téléphone 2',
    required: 'Les champs marqués * sont obligatoires',
    confirm: 'Confirmer les Préférences',
    saving: 'Enregistrement...',
    success: 'Préférences enregistrées',
  },
  ES: {
    privacySectionLabel: 'Información de privacidad',
    privacyTitle: 'Privacidad',
    privacyText: 'Autorizo el tratamiento de mis datos personales según el Reglamento UE 2016/679 RGPD',
    privacyRequired: 'El consentimiento es obligatorio',
    optionsTitle: '¿Qué aceptas recibir?',
    survey: 'Encuestas y cuestionarios',
    surveyDesc: 'Estudios de mercado y cuestionarios de productos',
    marketing: 'Acciones de marketing',
    marketingDesc: 'Newsletter, ofertas promocionales y novedades LoJack',
    accepted: 'Aceptado',
    rejected: 'Rechazado',
    tapToAccept: 'Toca para aceptar',
    contactMethodTitle: '¿En qué canal quieres ser contactado?',
    contactMethodHint: 'Elegirás el canal solo si aceptaste al menos una opción anterior',
    viewPrivacyLink: 'Ver la Información de privacidad',
    contactChannelsHint: 'Puedes seleccionar uno o varios canales. Se utilizarán para las comunicaciones de marketing que has aceptado arriba.',
    email: 'E-mail',
    phone: 'Teléfono',
    phone1: 'Teléfono 1',
    phone2: 'Teléfono 2',
    required: 'Los campos marcados con * son obligatorios',
    confirm: 'Confirmar Preferencias',
    saving: 'Guardando...',
    success: 'Preferencias guardadas',
  },
  DE: {
    privacySectionLabel: 'Datenschutzinformation',
    privacyTitle: 'Datenschutz',
    privacyText: 'Ich autorisiere die Verarbeitung meiner personenbezogenen Daten gemäß EU-Verordnung 2016/679 DSGVO',
    privacyRequired: 'Datenschutzeinwilligung ist erforderlich',
    optionsTitle: 'Was möchten Sie erhalten?',
    survey: 'Umfragen und Fragebögen',
    surveyDesc: 'Marktforschung und Produktfragebögen',
    marketing: 'Marketing-Aktionen',
    marketingDesc: 'Newsletter, Werbeangebote und LoJack-Neuigkeiten',
    accepted: 'Akzeptiert',
    rejected: 'Abgelehnt',
    tapToAccept: 'Tippen zum Akzeptieren',
    contactMethodTitle: 'Auf welchem Kanal möchten Sie kontaktiert werden?',
    contactMethodHint: 'Sie wählen den Kanal nur, wenn Sie mindestens eine Option oben akzeptiert haben',
    viewPrivacyLink: 'Datenschutzinformation einsehen',
    contactChannelsHint: 'Sie können einen oder mehrere Kanäle auswählen. Sie werden für die oben akzeptierten Marketing-Kommunikationen verwendet.',
    email: 'E-Mail',
    phone: 'Telefon',
    phone1: 'Telefon 1',
    phone2: 'Telefon 2',
    required: 'Mit * markierte Felder sind Pflichtfelder',
    confirm: 'Präferenzen bestätigen',
    saving: 'Speichern...',
    success: 'Präferenzen gespeichert',
  },
  PL: {
    privacySectionLabel: 'Informacja o prywatności',
    privacyTitle: 'Prywatność',
    privacyText: 'Wyrażam zgodę na przetwarzanie moich danych osobowych zgodnie z Rozporządzeniem UE 2016/679 RODO',
    privacyRequired: 'Zgoda na prywatność jest wymagana',
    optionsTitle: 'Co akceptujesz otrzymywać?',
    survey: 'Ankiety i kwestionariusze',
    surveyDesc: 'Badania rynku i ankiety produktowe',
    marketing: 'Działania marketingowe',
    marketingDesc: 'Newsletter, oferty promocyjne i nowości LoJack',
    accepted: 'Zaakceptowane',
    rejected: 'Odrzucone',
    tapToAccept: 'Dotknij, aby zaakceptować',
    contactMethodTitle: 'Na jakim kanale chcesz być kontaktowany?',
    contactMethodHint: 'Wybierzesz kanał tylko jeśli zaakceptowałeś co najmniej jedną opcję powyżej',
    viewPrivacyLink: 'Wyświetl informację o prywatności',
    contactChannelsHint: 'Możesz wybrać jeden lub więcej kanałów. Zostaną one wykorzystane do komunikacji marketingowej zaakceptowanej powyżej.',
    email: 'E-mail',
    phone: 'Telefon',
    phone1: 'Telefon 1',
    phone2: 'Telefon 2',
    required: 'Pola oznaczone * są obowiązkowe',
    confirm: 'Potwierdź preferencje',
    saving: 'Zapisywanie...',
    success: 'Preferencje zapisane',
  },
  PT: {
    privacySectionLabel: 'Informação de privacidade',
    privacyTitle: 'Privacidade',
    privacyText: 'Autorizo o tratamento dos meus dados pessoais nos termos do Regulamento UE 2016/679 RGPD',
    privacyRequired: 'O consentimento de privacidade é obrigatório',
    optionsTitle: 'O que aceita receber?',
    survey: 'Inquéritos e questionários',
    surveyDesc: 'Estudos de mercado e questionários de produto',
    marketing: 'Ações de marketing',
    marketingDesc: 'Newsletter, ofertas promocionais e novidades LoJack',
    accepted: 'Aceite',
    rejected: 'Recusado',
    tapToAccept: 'Toque para aceitar',
    contactMethodTitle: 'Em que canal quer ser contactado?',
    contactMethodHint: 'Escolherá o canal apenas se tiver aceitado pelo menos uma opção acima',
    viewPrivacyLink: 'Ver a informação de privacidade',
    contactChannelsHint: 'Pode escolher um ou mais canais. Serão utilizados para as comunicações de marketing aceites acima.',
    email: 'E-mail',
    phone: 'Telefone',
    phone1: 'Telefone 1',
    phone2: 'Telefone 2',
    required: 'Os campos marcados com * são obrigatórios',
    confirm: 'Confirmar preferências',
    saving: 'A guardar...',
    success: 'Preferências guardadas',
  },
  NL: {
    privacySectionLabel: 'Privacyverklaring',
    privacyTitle: 'Privacy',
    privacyText: 'Ik geef toestemming voor de verwerking van mijn persoonsgegevens volgens EU-verordening 2016/679 AVG',
    privacyRequired: 'Privacy-toestemming is verplicht',
    optionsTitle: 'Wat wilt u ontvangen?',
    survey: 'Enquêtes en vragenlijsten',
    surveyDesc: 'Marktonderzoek en productvragenlijsten',
    marketing: 'Marketingacties',
    marketingDesc: 'Nieuwsbrief, promotionele aanbiedingen en LoJack-nieuws',
    accepted: 'Geaccepteerd',
    rejected: 'Geweigerd',
    tapToAccept: 'Tik om te accepteren',
    contactMethodTitle: 'Via welk kanaal wilt u gecontacteerd worden?',
    contactMethodHint: 'U kunt het kanaal alleen kiezen als u hierboven minstens één optie hebt geaccepteerd',
    viewPrivacyLink: 'Privacyverklaring bekijken',
    contactChannelsHint: 'U kunt één of meer kanalen kiezen. Ze worden gebruikt voor de hierboven geaccepteerde marketingcommunicatie.',
    email: 'E-mail',
    phone: 'Telefoon',
    phone1: 'Telefoon 1',
    phone2: 'Telefoon 2',
    required: 'Velden gemarkeerd met * zijn verplicht',
    confirm: 'Voorkeuren bevestigen',
    saving: 'Opslaan...',
    success: 'Voorkeuren opgeslagen',
  },
  BE: {
    privacySectionLabel: "Information sur la confidentialité",
    privacyTitle: 'Confidentialité',
    privacyText: "J'autorise le traitement de mes données personnelles conformément au Règlement UE 2016/679 RGPD",
    privacyRequired: 'Le consentement est obligatoire',
    optionsTitle: 'Que souhaitez-vous recevoir ?',
    survey: 'Sondages et questionnaires',
    surveyDesc: 'Études de marché et questionnaires produits',
    marketing: 'Actions marketing',
    marketingDesc: 'Newsletter, offres promotionnelles et actualités LoJack',
    accepted: 'Accepté',
    rejected: 'Refusé',
    tapToAccept: 'Touchez pour accepter',
    contactMethodTitle: 'Sur quel canal souhaitez-vous être contacté ?',
    contactMethodHint: 'Vous choisirez le canal uniquement si vous avez accepté au moins une option ci-dessus',
    viewPrivacyLink: "Voir l'Information sur la confidentialité",
    contactChannelsHint: 'Vous pouvez sélectionner un ou plusieurs canaux. Ils seront utilisés pour les communications marketing acceptées ci-dessus.',
    email: 'E-mail',
    phone: 'Téléphone',
    phone1: 'Téléphone 1',
    phone2: 'Téléphone 2',
    required: 'Les champs marqués * sont obligatoires',
    confirm: 'Confirmer les Préférences',
    saving: 'Enregistrement...',
    success: 'Préférences enregistrées',
  },
  SE: null, // alias UK (fallback EN)
  NO: null, // alias UK (fallback EN)
  FI: null, // alias UK (fallback EN)
  DK: null, // alias UK (fallback EN)
  CZ: null, // alias UK (fallback EN)
  GR: null, // alias UK (fallback EN)
  CH: null, // alias DE
  GBZ: null, // alias UK
  IRL: null, // alias UK
};

// Resolve aliases. Native (req. Fabrizio 08/06/2026): IT/UK/FR/ES/DE/PL.
// BE→FR (native). CH→DE. GBZ/IRL→UK. NL/PT/SE/NO/FI/DK/CZ/GR → UK fallback.
GDPR_TRANSLATIONS.CH = GDPR_TRANSLATIONS.DE;
GDPR_TRANSLATIONS.GBZ = GDPR_TRANSLATIONS.UK;
GDPR_TRANSLATIONS.IRL = GDPR_TRANSLATIONS.UK;
// PT and NL promoted to native packs on 28/06/2026 — they're NOT in
// this UK alias list anymore.
['SE', 'NO', 'FI', 'DK', 'CZ', 'GR'].forEach(code => {
  GDPR_TRANSLATIONS[code] = GDPR_TRANSLATIONS.UK;
});

// Resolve translation for country code (defaults to UK English).
const getTranslation = (country) => {
  const code = (country || 'UK').toUpperCase();
  return GDPR_TRANSLATIONS[code] || GDPR_TRANSLATIONS.UK;
};

export const GDPRPreferences = ({ customerId, customerEmail, country, onComplete, customer, contractPdfUrl, privacyPolicyUrl }) => {
  const t = getTranslation(country);

  // Build dynamic contact channel options. Email always included; Phone 1 (from
  // dealer) and Phone 2 (added by customer in prior step) when present.
  const fmtPhone = (prefix, number) => `${prefix || ''} ${number || ''}`.trim();
  const channels = [];
  if (customerEmail) {
    channels.push({ key: 'email', icon: Mail, label: t.email, value: customerEmail });
  }
  if (customer?.phone) {
    channels.push({
      key: 'phone',
      icon: Phone,
      label: customer?.phone2 ? t.phone1 : t.phone,
      value: fmtPhone(customer.phone_prefix, customer.phone),
    });
  }
  if (customer?.phone2) {
    channels.push({
      key: 'phone2',
      icon: Phone,
      label: t.phone2,
      value: fmtPhone(customer.phone2_prefix, customer.phone2),
    });
  }

  // Req. Zorzutti Stand-up 22/05/2026:
  //   - Privacy obbligatoria (checkbox classica)
  //   - 2 opzioni Accept/Reject: Surveys + Marketing
  //   - Modalità di contatto visibile SOLO se almeno una opzione è Accept
  //   - Conferma → mostra "Preferenze salvate" per 1s poi prosegue
  //
  // Req. Mara Capelli Stand-up 26/05/2026 punto 12:
  //   - Il cliente può selezionare PIÙ canali contemporaneamente (email + phone)
  //     invece di uno solo. Implementato come tap-to-toggle multi-select.
  //     Default: il primo canale disponibile è pre-selezionato.
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [acceptSurvey, setAcceptSurvey] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  const showContactMethod = acceptSurvey || acceptMarketing;

  // Req. Zorzutti 27/05/2026 — visione obbligatoria del PDF privacy
  // PRIMA che il checkbox possa essere spuntato. Timer 15s che parte
  // appena la URL del PDF è disponibile (NON su onLoad dell'iframe —
  // i PDF cross-origin non firano onLoad in modo affidabile, stesso
  // problema noto risolto in ContractModal Mara req. #23).
  const privacyIframeRef = useRef(null);
  // Scrollable wrapper ref for the desktop react-pdf path. See
  // ContractModal for the rationale (Fabrizio 11/06/2026 — scroll-end
  // detection must be reliable; native PDF viewers don't expose scroll).
  const privacyPdfScrollRef = useRef(null);
  // Desktop-vs-mobile heuristic kept in sync with the read-confirmation
  // hook so we pick the right PDF renderer (react-pdf on desktop, legacy
  // iframe + countdown on mobile).
  const [privacyIsDesktop, setPrivacyIsDesktop] = useState(() => {
    if (typeof window === "undefined") return true;
    try {
      const narrow = window.matchMedia?.("(max-width: 768px)")?.matches;
      const touch = "ontouchstart" in window;
      const ios = /iPad|iPhone|iPod/i.test(navigator.userAgent || "");
      return !(narrow || touch || ios);
    } catch (_) { return true; }
  });
  useEffect(() => {
    const onResize = () => {
      try {
        const narrow = window.matchMedia?.("(max-width: 768px)")?.matches;
        const touch = "ontouchstart" in window;
        const ios = /iPad|iPhone|iPod/i.test(navigator.userAgent || "");
        setPrivacyIsDesktop(!(narrow || touch || ios));
      } catch (_) { setPrivacyIsDesktop(true); }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const privacyPdfHref = privacyPolicyUrl || contractPdfUrl;

  // Whitelabel guard (Fabrizio req. 01/06/2026): in produzione il
  // frontend è su securewallet.lojack.eu ma il backend è su un host
  // Emergent diverso. Linkare/iframare il PDF backend direttamente
  // espone l'host Emergent nella barra URL del browser quando il
  // cliente clicca "Apri in nuova tab", VIOLANDO il branding LoJack.
  // Soluzione: scarichiamo il PDF via fetch → ObjectURL su blob, così
  // sia l'iframe che il link target=_blank usano un URL `blob:`
  // appartenente al dominio frontend (securewallet.lojack.eu/...).
  const [privacyPdfBlobUrl, setPrivacyPdfBlobUrl] = useState(null);
  // True when backend reports the privacy notice is not configured (404/503).
  const [privacyUnavailable, setPrivacyUnavailable] = useState(false);

  useEffect(() => {
    setPrivacyUnavailable(false);
    if (!privacyPdfHref) {
      setPrivacyPdfBlobUrl(null);
      return undefined;
    }
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const resp = await fetch(privacyPdfHref, { credentials: 'omit' });
        if (resp.status === 404 || resp.status === 503) {
          if (!cancelled) setPrivacyUnavailable(true);
          return;
        }
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        if (cancelled) return;
        // Force application/pdf MIME so Firefox/Safari render inline
        const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
        createdUrl = URL.createObjectURL(pdfBlob);
        setPrivacyPdfBlobUrl(createdUrl);
      } catch (e) {
        // Fallback to the direct backend URL — branding leak is the
        // lesser evil vs not showing the privacy notice at all.
        if (!cancelled) setPrivacyPdfBlobUrl(privacyPdfHref);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [privacyPdfHref]);

  // Used by iframe + "open in new tab" link — same-origin blob URL
  // when available, fallback to the raw backend URL otherwise.
  const privacyPdfDisplaySrc = privacyPdfBlobUrl || privacyPdfHref;

  // New read-confirmation hook (req. A. Zorzutti 05/06/2026 + Fabrizio
  // 11/06/2026 scroll-reliability fix). Desktop scrolls a real HTML
  // container rendered by <ScrollablePdfReader>; mobile keeps the legacy
  // iframe + 15s countdown safety.
  const {
    mode: privacyReadMode,
    readDone: privacyReadDone,
    secondsRemaining: privacyReadRemaining,
    progressPct: privacyReadProgress,
  } = usePdfReadConfirmation({
    iframeRef: privacyIframeRef,
    scrollContainerRef: privacyPdfScrollRef,
    minSeconds: PRIVACY_MIN_READ_SECONDS,
    enabled: !!privacyPdfHref,
  });

  // If the user un-checks "Privacy*" after having read it, keep the
  // unlocked state — the read is persistent for the session.
  const canCheckPrivacy = privacyReadDone;

  const initialChannel = channels[0]?.key || 'email';
  const [selectedChannels, setSelectedChannels] = useState(new Set([initialChannel]));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Confirm-dialog state (req. A. Zorzutti pt. 14: pop-up di conferma
  // quando il cliente rifiuta TUTTE le comunicazioni opzionali).
  const [showRejectAllDialog, setShowRejectAllDialog] = useState(false);

  // The customer is "rejecting all optional comms" when BOTH marketing
  // and survey are off AND no contact channel is selected. The mandatory
  // privacy checkbox is NOT counted because it is mandatory.
  const isRejectingAllOptional = () =>
    !acceptSurvey && !acceptMarketing &&
    (selectedChannels.size === 0 || !showContactMethod);

  const toggleChannel = (key) => {
    setSelectedChannels(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        // Don't allow deselecting the last channel — at least one required.
        if (next.size > 1) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!privacyAccepted) return;
    if (showContactMethod && selectedChannels.size === 0) return;
    // Req. A. Zorzutti pt. 14: when the user is rejecting ALL optional
    // comms, surface a confirmation dialog BEFORE persisting. The dialog
    // is single (generic), per design decision Stand-up 04/06/2026.
    if (isRejectingAllOptional() && !showRejectAllDialog) {
      setShowRejectAllDialog(true);
      return;
    }
    await _doSubmit();
  };

  const _doSubmit = async () => {
    setShowRejectAllDialog(false);
    setSaving(true);
    try {
      // Map the new UI model into the backend payload. The "lists" array
      // preserves backwards-compatibility with the existing endpoint:
      // 'survey' and 'marketing' are present if accepted, missing if rejected.
      const lists = [];
      if (acceptSurvey) lists.push('survey');
      if (acceptMarketing) lists.push('marketing');
      // Req. Mara Capelli 26/05/2026 punto 12: multi-channel. Inviamo la lista
      // completa via `contact_channels`. Per retro-compatibilità manteniamo
      // anche `contact_method` con il primo canale selezionato.
      const channelList = Array.from(selectedChannels);
      const primaryChannel = channelList[0] || 'email';
      const primaryValue = channels.find(c => c.key === primaryChannel)?.value || customerEmail || '';
      const channelValues = channelList.map(k => ({
        channel: k,
        value: channels.find(c => c.key === k)?.value || '',
      }));
      await axios.post(`${API}/customers/${customerId}/gdpr-preferences`, {
        contact_method: showContactMethod ? primaryChannel : null,
        contact_value: showContactMethod ? primaryValue : null,
        contact_channels: showContactMethod ? channelValues : null,
        lists,
        subscribe: lists.length > 0,
        privacy_accepted: privacyAccepted,
      });
      setSaved(true);
      // Show "Preferenze salvate" for exactly 1 second, then advance.
      setTimeout(() => onComplete(), 1000);
    } catch {
      // silently continue (no error UI in original flow)
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <div className="text-center py-8" data-testid="gdpr-saved-screen">
        <CheckCircle size={48} className="text-lojack-blue-500 mx-auto mb-3" />
        <p className="text-lojack-blue-400 font-semibold text-lg">{t.success}</p>
      </div>
    );
  }

  // Pure tap-to-toggle card (no visible checkbox). The card itself toggles
  // between Accept (red bordered, ✓ icon) and Reject (grey bordered, × icon).
  const ToggleOption = ({ id, accepted, onToggle, label, desc }) => (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
        accepted
          ? 'border-lojack-red bg-lojack-red/10'
          : 'border-gray-700 hover:border-gray-500 bg-[#0A0A0A]'
      }`}
      data-testid={`gdpr-toggle-${id}`}
      aria-pressed={accepted}
    >
      <div className="flex items-start gap-3">
        <div
          className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5 ${
            accepted ? 'bg-lojack-red' : 'bg-gray-700'
          }`}
        >
          {accepted ? <CheckCircle size={14} className="text-white" /> : <X size={14} className="text-gray-400" />}
        </div>
        <div className="flex-1">
          <p className={`font-medium text-sm ${accepted ? 'text-white' : 'text-gray-300'}`}>{label}</p>
          {desc && <p className="text-gray-500 text-xs mt-0.5">{desc}</p>}
          <p
            className={`text-[10px] uppercase tracking-wider font-semibold mt-2 ${
              accepted ? 'text-lojack-red' : 'text-gray-500'
            }`}
          >
            {accepted ? t.accepted : t.rejected}
          </p>
        </div>
      </div>
    </button>
  );

  // Read-status strip (req. utente 06/2026): banner giallo per maggiore
  // visibilità. Renderizzato in due posizioni via CSS responsive: su
  // mobile sotto il subtitle, su desktop sotto il PDF. idSuffix keeps
  // data-testids unique across the two instances.
  const renderPrivacyReadStrip = (idSuffix = '') => (
    !privacyReadDone ? (
      <div className="px-3 py-2 bg-yellow-500/10 border border-yellow-500/40 rounded-lg" data-testid={`gdpr-privacy-read-wait${idSuffix}`}>
        <div className="flex items-center gap-2">
        {privacyReadMode === 'scroll'
          ? <ScrollText size={14} className="text-yellow-400 shrink-0" />
          : <Lock size={14} className="text-yellow-400 shrink-0" />}
        <p className="text-yellow-300 font-medium text-xs flex-1">
          {privacyReadMode === 'scroll'
            ? ({
                IT: "Scorri l'informativa fino in fondo per poter confermare la lettura",
                UK: "Scroll the privacy notice to the bottom to confirm reading",
                FR: "Faites défiler l'information jusqu'en bas pour confirmer la lecture",
                ES: "Desplázate hasta el final de la información para confirmar la lectura",
                DE: "Scrollen Sie die Information bis zum Ende",
                PL: "Przewiń informację o prywatności do końca, aby potwierdzić",
                PT: "Role a informação até ao fim para confirmar a leitura",
                NL: "Scroll naar het einde van de privacyverklaring",
                SE: "Bläddra ner till slutet av sekretessmeddelandet",
                NO: "Bla ned til slutten av personvernerklæringen",
                FI: "Vieritä tietosuojailmoitus loppuun vahvistaaksesi lukemisen",
                DK: "Rul privatlivsmeddelelsen til bunden for at bekræfte læsningen",
                CZ: "Posuňte informaci o ochraně osobních údajů až dolů",
                GR: "Κάντε κύλιση της ειδοποίησης απορρήτου μέχρι το τέλος",
                CH: "Scrollen Sie die Information bis zum Ende",
                BE: "Faites défiler l'information jusqu'en bas pour confirmer la lecture",
                GBZ: "Scroll the privacy notice to the bottom to confirm reading",
                IRL: "Scroll the privacy notice to the bottom to confirm reading",
              }[country] || "Scroll the privacy notice to the bottom to confirm reading")
            : ({
                IT: "Leggi l'informativa privacy per qualche secondo",
                UK: "Please read the privacy notice for a few seconds",
                FR: "Veuillez lire l'information sur la confidentialité",
                ES: "Por favor lea la información de privacidad",
                DE: "Bitte lesen Sie die Datenschutzinformation",
                PL: "Proszę przeczytać informację o prywatności przez kilka sekund",
                PT: "Por favor leia a informação de privacidade por alguns segundos",
                NL: "Lees de privacyverklaring een paar seconden",
                SE: "Vänligen läs sekretessmeddelandet några sekunder",
                NO: "Vennligst les personvernerklæringen i noen sekunder",
                FI: "Lue tietosuojailmoitus muutaman sekunnin ajan",
                DK: "Læs venligst privatlivsmeddelelsen i et par sekunder",
                CZ: "Přečtěte si prosím prohlášení o ochraně osobních údajů několik sekund",
                GR: "Διαβάστε την ειδοποίηση απορρήτου για μερικά δευτερόλεπτα",
                CH: "Bitte lesen Sie die Datenschutzinformation",
                BE: "Veuillez lire l'information sur la confidentialité",
                GBZ: "Please read the privacy notice for a few seconds",
                IRL: "Please read the privacy notice for a few seconds",
              }[country] || "Please read the privacy notice for a few seconds")}
          {privacyReadMode === 'countdown' && (
            <span className="ml-1 font-mono text-yellow-200">({privacyReadRemaining}s)</span>
          )}
        </p>
        </div>
        {/* Barra full-width sotto la stringa — schema armonizzato con
            lo Step 3 / ContractModal (req. Zorzutti 07/07/2026) */}
        <div className="mt-2 h-1 bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-yellow-400 transition-all duration-700"
            style={{ width: `${privacyReadProgress}%` }}
          />
        </div>
      </div>
    ) : (
      <div className="flex items-center gap-2 px-3 py-2 bg-lojack-blue-500/10 border border-lojack-blue-500/30 rounded-lg" data-testid={`gdpr-privacy-read-done${idSuffix}`}>
        <CheckCircle size={14} className="text-lojack-blue-400 shrink-0" />
        <p className="text-lojack-blue-300 text-xs">
          {({
            IT: "Informativa visionata — ora puoi accettare",
            UK: "Notice viewed — you can now accept",
            FR: "Information consultée — vous pouvez accepter",
            ES: "Información consultada — ahora puede aceptar",
            DE: "Information eingesehen — Sie können nun zustimmen",
            PL: "Informacja przeczytana — możesz teraz zaakceptować",
            PT: "Informação consultada — pode agora aceitar",
            NL: "Verklaring bekeken — u kunt nu accepteren",
            SE: "Meddelandet visat — du kan nu acceptera",
            NO: "Erklæringen vist — du kan nå akseptere",
            FI: "Ilmoitus luettu — voit nyt hyväksyä",
            DK: "Erklæringen vist — du kan nu acceptere",
            CZ: "Prohlášení zobrazeno — nyní můžete přijmout",
            GR: "Η ειδοποίηση εμφανίστηκε — μπορείτε τώρα να αποδεχτείτε",
            CH: "Information eingesehen — Sie können nun zustimmen",
            BE: "Information consultée — vous pouvez accepter",
            GBZ: "Notice viewed — you can now accept",
            IRL: "Notice viewed — you can now accept",
          }[country] || "Notice viewed — you can now accept")}
        </p>
      </div>
    )
  );

  return (
    <div className="space-y-5" data-testid="gdpr-preferences-form">
      {/* Label sezione — mockup Zorzutti 07/07/2026 (razionalizzazione
          testi: rimosso il doppio titolo "Preferenze di Contatto") */}
      <p className="text-white font-semibold text-sm" data-testid="gdpr-privacy-section-label">{t.privacySectionLabel}</p>

      {/* 1) Privacy Authorization (mandatory) — Req. Zorzutti 27/05/2026:
            visione obbligatoria del PDF prima di poter spuntare il checkbox.
            Pattern coerente con ContractModal: iframe inline + timer 15s. */}
      <div className="rounded-xl border border-gray-700 bg-[#0A0A0A] p-4 space-y-3">
        {/* PDF embed + reading timer */}
        {privacyPdfHref && (
          <div className="space-y-2" data-testid="gdpr-privacy-pdf-block">
            {/* Mobile: strip in cima al riquadro (mockup Zorzutti 07/07/2026) */}
            <div className="md:hidden">{!privacyUnavailable && renderPrivacyReadStrip('-top')}</div>
            <div className={`flex items-center justify-end gap-2 flex-wrap ${privacyUnavailable ? 'hidden' : ''}`}>
              <a
                href={privacyPdfDisplaySrc}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                data-testid="gdpr-view-privacy-link"
              >
                <Eye size={12} />
                {t.viewPrivacyLink}
              </a>
            </div>

            {/* Desktop = react-pdf in a scrollable HTML wrapper (reliable
                scroll-end detection). Mobile = legacy iframe + 15s
                countdown safety. Fabrizio 11/06/2026.
                Note: pass the raw backend URL (not blob:) so the CSP
                connect-src whitelist doesn't block the XHR. */}
            {privacyUnavailable ? (
              <PrivacyUnavailableNotice country={country} />
            ) : privacyIsDesktop ? (
              <ScrollablePdfReader
                ref={privacyPdfScrollRef}
                fileUrl={privacyPdfHref}
                height="60vh"
                minHeight={400}
                errorFallbackHref={privacyPdfDisplaySrc}
                errorFallbackLabel={t.viewPrivacyLink}
                loadingLabel={
                  ({ IT: 'Caricamento PDF...', UK: 'Loading PDF...', FR: 'Chargement du PDF...',
                     ES: 'Cargando PDF...', DE: 'PDF wird geladen...', PL: 'Ładowanie PDF...' })[
                    (country || 'UK').toUpperCase()
                  ] || 'Loading PDF...'
                }
                errorLabel={
                  ({ IT: 'Impossibile caricare il PDF.', UK: 'Unable to load the PDF.',
                     FR: 'Impossible de charger le PDF.', ES: 'No se puede cargar el PDF.',
                     DE: 'PDF kann nicht geladen werden.', PL: 'Nie można załadować pliku PDF.' })[
                    (country || 'UK').toUpperCase()
                  ] || 'Unable to load the PDF.'
                }
                retryLabel={
                  ({ IT: 'Riprova', UK: 'Retry', FR: 'Réessayer',
                     ES: 'Reintentar', DE: 'Erneut versuchen', PL: 'Spróbuj ponownie' })[
                    (country || 'UK').toUpperCase()
                  ] || 'Retry'
                }
              />
            ) : (
              <iframe
                ref={privacyIframeRef}
                src={`${privacyPdfDisplaySrc}#toolbar=0&navpanes=0`}
                title="Privacy Notice"
                className="w-full rounded-lg border border-gray-700 bg-white"
                style={{ height: '60vh', minHeight: '400px' }}
                data-testid="gdpr-privacy-iframe"
              />
            )}

            {/* Read-progress strip — posizione desktop; su mobile la
                stessa strip è renderizzata sotto il subtitle in alto. */}
            <div className="hidden md:block">{!privacyUnavailable && renderPrivacyReadStrip('')}</div>
          </div>
        )}

        {/* Privacy* checkbox — disabled until the PDF has been viewed */}
        <label className={`flex items-start gap-3 ${canCheckPrivacy ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
          <input
            type="checkbox"
            checked={privacyAccepted}
            onChange={(e) => canCheckPrivacy && setPrivacyAccepted(e.target.checked)}
            disabled={!canCheckPrivacy}
            className="checkbox-lojack mt-1"
            data-testid="gdpr-privacy-checkbox"
          />
          <div>
            <span className="text-white text-sm font-medium">{t.privacyTitle}*</span>
            <p className="text-gray-400 text-xs mt-1 leading-relaxed">"{t.privacyText}"</p>
          </div>
        </label>
      </div>

      {/* 2 + 3) Surveys and Marketing — tap-to-toggle, no checkbox */}
      <div className="space-y-3">
        <p className="text-white text-sm font-medium">{t.optionsTitle}</p>
        <ToggleOption
          id="survey"
          accepted={acceptSurvey}
          onToggle={() => setAcceptSurvey(v => !v)}
          label={t.survey}
          desc={t.surveyDesc}
        />
        <ToggleOption
          id="marketing"
          accepted={acceptMarketing}
          onToggle={() => setAcceptMarketing(v => !v)}
          label={t.marketing}
          desc={t.marketingDesc}
        />
      </div>

      {/* 4) Contact channels — visible only if at least one option above is
          accepted. Req. Mara Capelli 26/05/2026 punto 12: multi-select
          (email + telefono insieme), non più radio. Almeno un canale è
          obbligatorio quando il cliente ha accettato survey o marketing. */}
      {showContactMethod && (
        <div className="animate-fade-in" data-testid="gdpr-contact-section">
          <p className="text-white text-sm font-medium mb-2">{t.contactMethodTitle}*</p>
          <p className="text-gray-500 text-xs mb-3">{t.contactChannelsHint || t.contactMethodHint}</p>
          <div className={`grid gap-3 ${channels.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {channels.map((ch) => {
              const Icon = ch.icon;
              const active = selectedChannels.has(ch.key);
              return (
                <button
                  key={ch.key}
                  type="button"
                  onClick={() => toggleChannel(ch.key)}
                  className={`p-3 rounded-xl border-2 transition-all flex flex-col items-start gap-1 text-left ${
                    active ? 'border-lojack-red bg-red-900/10' : 'border-gray-700 hover:border-gray-600'
                  }`}
                  data-testid={`gdpr-contact-${ch.key}`}
                  aria-pressed={active}
                >
                  <div className="flex items-center gap-2 w-full">
                    <Icon size={16} className={active ? 'text-lojack-red' : 'text-gray-500'} />
                    <span className={`text-xs uppercase tracking-wider ${active ? 'text-white font-semibold' : 'text-gray-400'}`}>
                      {ch.label}
                    </span>
                    {active && (
                      <CheckCircle size={14} className="text-lojack-red ml-auto" />
                    )}
                  </div>
                  <span className={`text-xs truncate w-full ${active ? 'text-gray-200' : 'text-gray-500'}`}>{ch.value}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-gray-600 text-xs">{t.required}</p>

      <button
        onClick={handleSubmit}
        disabled={!privacyAccepted || (showContactMethod && selectedChannels.size === 0) || saving}
        className="btn-lojack w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="gdpr-submit"
      >
        {saving ? <><div className="spinner-small" /> {t.saving}</> : <><CheckCircle size={18} /> {t.confirm}</>}
      </button>

      {/* Reject-all-optional confirmation dialog (req. A. Zorzutti pt. 14) */}
      {showRejectAllDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fade-in"
          data-testid="gdpr-reject-all-dialog"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-[#0f0f12] border border-lojack-clause-500/40 rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-3">
              <div className="p-2 bg-lojack-clause-500/15 rounded-lg shrink-0">
                <Shield size={20} className="text-lojack-clause-300" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-white" data-testid="gdpr-reject-all-dialog-title">
                  {({
                    IT: 'Conferma rifiuto comunicazioni',
                    UK: 'Confirm refusal of communications',
                    FR: 'Confirmer le refus des communications',
                    ES: 'Confirmar rechazo de comunicaciones',
                    DE: 'Ablehnung der Kommunikation bestätigen',
                    PL: 'Potwierdź odmowę komunikacji',
                    PT: 'Confirmar recusa de comunicações',
                    NL: 'Bevestig weigering van communicatie',
                    SE: 'Bekräfta avslag av kommunikation',
                    NO: 'Bekreft avslag av kommunikasjon',
                    FI: 'Vahvista viestinnän kieltäytyminen',
                    DK: 'Bekræft afslag på kommunikation',
                    CZ: 'Potvrďte odmítnutí komunikace',
                    GR: 'Επιβεβαίωση άρνησης επικοινωνιών',
                    CH: 'Ablehnung der Kommunikation bestätigen',
                    BE: 'Confirmer le refus des communications',
                    GBZ: 'Confirm refusal of communications',
                    IRL: 'Confirm refusal of communications',
                  }[country] || 'Confirm refusal of communications')}
                </h3>
              </div>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed mb-5">
              {({
                IT: 'È sicuro di non voler ricevere comunicazioni da parte di LoJack? In caso di conferma, non riceverà aggiornamenti su nuove funzionalità del servizio, promozioni riservate o richieste di feedback. Sarà comunque possibile modificare la propria scelta in qualsiasi momento dalle impostazioni del proprio profilo.',
                UK: 'Are you sure you do not want to receive communications from LoJack? If you confirm, you will not receive updates on new service features, exclusive offers or feedback requests. You can change your preferences at any time from your account settings.',
                FR: "Êtes-vous sûr(e) de ne pas vouloir recevoir de communications de LoJack ? En cas de confirmation, vous ne recevrez pas de mises à jour sur les nouvelles fonctionnalités, les offres réservées ou les demandes de feedback. Vous pourrez toujours modifier votre choix à tout moment depuis les paramètres de votre profil.",
                ES: 'Está seguro/a de no querer recibir comunicaciones de LoJack? Si confirma, no recibirá actualizaciones sobre nuevas funcionalidades, promociones reservadas o solicitudes de feedback. Podrá modificar su elección en cualquier momento desde la configuración de su perfil.',
                DE: 'Sind Sie sicher, dass Sie keine Mitteilungen von LoJack erhalten möchten? Bei Bestätigung erhalten Sie keine Updates zu neuen Funktionen, exklusiven Angeboten oder Feedback-Anfragen. Sie können Ihre Auswahl jederzeit in den Profileinstellungen ändern.',
                PL: 'Czy na pewno nie chce Pan/Pani otrzymywać komunikacji od LoJack? W przypadku potwierdzenia nie będą Państwo otrzymywać aktualizacji o nowych funkcjonalnościach usługi, promocjach ani prośbach o opinie. W każdej chwili można zmienić swój wybór w ustawieniach profilu.',
                PT: 'Tem a certeza de que não pretende receber comunicações da LoJack? Em caso de confirmação, não receberá atualizações sobre novas funcionalidades, promoções reservadas ou pedidos de feedback. Poderá modificar a sua escolha a qualquer momento nas definições do seu perfil.',
                NL: 'Weet u zeker dat u geen communicatie van LoJack wilt ontvangen? Als u bevestigt, ontvangt u geen updates over nieuwe functies, exclusieve aanbiedingen of feedbackverzoeken. U kunt uw keuze op elk moment wijzigen via de profielinstellingen.',
                SE: 'Är du säker på att du inte vill ta emot kommunikation från LoJack? Vid bekräftelse kommer du inte att få uppdateringar om nya funktioner, exklusiva erbjudanden eller feedback-förfrågningar. Du kan när som helst ändra ditt val i profilinställningarna.',
                NO: 'Er du sikker på at du ikke ønsker å motta kommunikasjon fra LoJack? Hvis du bekrefter, vil du ikke motta oppdateringer om nye funksjoner, eksklusive tilbud eller forespørsler om tilbakemelding. Du kan endre valget ditt når som helst i profilinnstillingene.',
                FI: 'Oletko varma, että et halua vastaanottaa viestintää LoJackilta? Vahvistuksen jälkeen et saa päivityksiä uusista ominaisuuksista, eksklusiivisista tarjouksista tai palautekyselyistä. Voit muuttaa valintaa milloin tahansa profiiliasetuksissa.',
                DK: 'Er du sikker på, at du ikke ønsker at modtage kommunikation fra LoJack? Hvis du bekræfter, vil du ikke modtage opdateringer om nye funktioner, eksklusive tilbud eller anmodninger om feedback. Du kan til enhver tid ændre dit valg i profilindstillingerne.',
                CZ: 'Jste si jistý/á, že nechcete dostávat komunikaci od LoJack? V případě potvrzení nebudete dostávat aktualizace o nových funkcích, exkluzivních nabídkách ani žádosti o zpětnou vazbu. Své rozhodnutí můžete kdykoli změnit v nastavení profilu.',
                GR: 'Είστε σίγουρος/η ότι δεν θέλετε να λαμβάνετε επικοινωνίες από τη LoJack; Σε περίπτωση επιβεβαίωσης, δεν θα λαμβάνετε ενημερώσεις σχετικά με νέες λειτουργίες, αποκλειστικές προσφορές ή αιτήματα ανατροφοδότησης. Μπορείτε να αλλάξετε την επιλογή σας ανά πάσα στιγμή από τις ρυθμίσεις του προφίλ σας.',
                CH: 'Sind Sie sicher, dass Sie keine Mitteilungen von LoJack erhalten möchten? Bei Bestätigung erhalten Sie keine Updates zu neuen Funktionen, exklusiven Angeboten oder Feedback-Anfragen. Sie können Ihre Auswahl jederzeit in den Profileinstellungen ändern.',
                BE: "Êtes-vous sûr(e) de ne pas vouloir recevoir de communications de LoJack ? En cas de confirmation, vous ne recevrez pas de mises à jour sur les nouvelles fonctionnalités, les offres réservées ou les demandes de feedback. Vous pourrez toujours modifier votre choix à tout moment depuis les paramètres de votre profil.",
                GBZ: 'Are you sure you do not want to receive communications from LoJack? If you confirm, you will not receive updates on new service features, exclusive offers or feedback requests. You can change your preferences at any time from your account settings.',
                IRL: 'Are you sure you do not want to receive communications from LoJack? If you confirm, you will not receive updates on new service features, exclusive offers or feedback requests. You can change your preferences at any time from your account settings.',
              }[country] || 'Are you sure you do not want to receive communications from LoJack? You can change your preferences at any time from your account settings.')}
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowRejectAllDialog(false)}
                className="px-4 py-2 rounded-lg border border-gray-700 text-gray-300 hover:border-gray-500 hover:text-white transition text-sm"
                data-testid="gdpr-reject-all-dialog-cancel"
              >
                {({
                  IT: 'Torna indietro', UK: 'Go back', FR: 'Retour', ES: 'Volver',
                  DE: 'Zurück', PL: 'Wróć', PT: 'Voltar', NL: 'Terug', SE: 'Tillbaka',
                  NO: 'Tilbake', FI: 'Takaisin', DK: 'Tilbage', CZ: 'Zpět', GR: 'Πίσω',
                  CH: 'Zurück', BE: 'Retour', GBZ: 'Go back', IRL: 'Go back',
                }[country] || 'Go back')}
              </button>
              <button
                type="button"
                onClick={_doSubmit}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-lojack-clause-600 hover:bg-lojack-clause-700 text-white font-semibold transition text-sm disabled:opacity-50"
                data-testid="gdpr-reject-all-dialog-confirm"
              >
                {({
                  IT: 'Sì, confermo', UK: 'Yes, confirm', FR: 'Oui, confirmer',
                  ES: 'Sí, confirmar', DE: 'Ja, bestätigen', PL: 'Tak, potwierdzam',
                  PT: 'Sim, confirmar', NL: 'Ja, bevestigen', SE: 'Ja, bekräfta',
                  NO: 'Ja, bekreft', FI: 'Kyllä, vahvistan', DK: 'Ja, bekræft',
                  CZ: 'Ano, potvrzuji', GR: 'Ναι, επιβεβαιώνω',
                  CH: 'Ja, bestätigen', BE: 'Oui, confirmer',
                  GBZ: 'Yes, confirm', IRL: 'Yes, confirm',
                }[country] || 'Yes, confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
